import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AllSystemCSSProperties } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { Ref, useImperativeHandle } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Tabs from '../wrappers/Tabs';
import { ISupportedFieldType } from './FieldTypes';
import FieldWrapper from './FieldWrapper';
import MultiRowGenerator from './MultiRowGenerator';
import SimpleFieldRenderer, { ISimpleFieldRendererProps } from './SimpleFieldRenderer';
import { getFullNameOfField, getValuesFromProps, ISupportedValueType } from './utils';

const StyledBox = styled(Box)`
    margin: 0;
    display: flex;
    flex-wrap: wrap;
`;

const StyledTabsWrapper = styled(Box)(
    ({ theme }) => `
    margin-left: ${theme.spacing(1)};
    margin-right: ${theme.spacing(1)};
    width: 100%;
    display: inline-flex;
    flex-direction: column;
`
);

type ICallableRef = {
    getValues: () => ISupportedValueType;
};

export type IFieldsGeneratorProps = Pick<
    ISimpleFieldRendererProps,
    'disabled' | 'enableDOMRequired' | 'readOnly' | 'shouldUnregister' | 'fullWidth'
> & {
    containerName?: string;
    innerRef?: Ref<ICallableRef>;
    name?: string;
    actions?: boolean;
    fields: (false | ISupportedFieldType)[];
    justifyContent?: AllSystemCSSProperties['justifyContent'];
};

export const FieldsGenerator = ({
    innerRef,
    name,
    actions = false,
    containerName,
    fields,
    ...rest
}: IFieldsGeneratorProps) => {
    const { control, getValues } = useFormContext();
    const fieldsToRender = fields.filter((field) => typeof field === 'object') as ISupportedFieldType[];

    useImperativeHandle(innerRef, () => ({
        getValues: () => {
            return getValuesFromProps(fieldsToRender);
        }
    }));

    const toRender = fieldsToRender.map((field) => {
        if (field.type === 'newLine') {
            return <Box key={field.props.name} sx={{ width: '100%' }} />;
        } else if (field.type === 'multiRowInputs') {
            return (
                <FieldWrapper
                    key={`multiRowInputs_${field.props.name}`}
                    type="multiRowInputs"
                    fullWidth
                    hidden={field.display ? field.display(getValues()) : true}
                >
                    <MultiRowGenerator
                        {...rest}
                        key={field.props.name}
                        fixedSize={field.props.fixedSize}
                        inputs={field.props.inputs}
                        name={field.props.name}
                        sx={field.sx}
                        fullWidth={rest.fullWidth}
                    />
                </FieldWrapper>
            );
        } else if (field.type === 'block') {
            return (
                // <Box key={field.props.name} sx={{...(field.props.withBorder !== false ? {borderTop: '1px solid'}:{}), pt: 3, width: 1}}>
                <Box key={field.props.name} sx={{ pt: 3, width: 1 }}>
                    <Typography variant="h6" sx={{ ml: 1, fontWeight: 400 }}>
                        {field.props.title}
                    </Typography>
                    <Box sx={{ borderTop: '1px solid', borderColor: 'lightgray' }}>
                        <FieldsGenerator
                            {...rest}
                            key={field.props.name}
                            containerName={field.props.wrapNameOfFields !== false ? field.props.name : undefined}
                            fields={field.props.fields}
                            name={field.props.name}
                            fullWidth={true}
                        />
                    </Box>
                </Box>
            );
        } else if (field.type === 'tabs') {
            return (
                <Controller
                    key={field.props.name}
                    name={field.props.name}
                    control={control}
                    rules={{
                        deps: field.deps
                    }}
                    render={({ field: { ref: _, ...fieldProps } }) => {
                        const hideTabs = !(typeof field.display === 'function'
                            ? field.display(getValues())
                            : field.display !== false);

                        if (!field.props.tabs.length) {
                            return <></>;
                        }

                        return (
                            <StyledTabsWrapper
                                sx={{
                                    ...(hideTabs ? { display: 'none' } : {})
                                }}
                            >
                                <Tabs
                                    {...field.props}
                                    onChange={fieldProps.onChange}
                                    tabs={field.props.tabs.map((tab) => ({
                                        id: tab.name,
                                        label: tab.label,
                                        display: tab.display ? tab.display(getValues()) : true,
                                        content: (
                                            <FieldsGenerator
                                                {...rest}
                                                key={tab.name}
                                                fields={tab.inputs}
                                                name={tab.name}
                                                fullWidth={true}
                                            />
                                        )
                                    }))}
                                />
                            </StyledTabsWrapper>
                        );
                    }}
                ></Controller>
            );
        } else {
            if (actions) {
                return (
                    <Box key={field.props.name}>
                        <SimpleFieldRenderer {...rest} input={field} fullName={getFullNameOfField(field.props.name)} />
                    </Box>
                );
            } else {
                return (
                    <SimpleFieldRenderer
                        {...rest}
                        key={field.props.name}
                        input={field}
                        fullName={getFullNameOfField(field.props.name, containerName)}
                    />
                );
            }
        }
    });

    if (actions) {
        return <>{toRender}</>;
    } else {
        return (
            <StyledBox
                data-testid={name ?? 'fieldsGenerator'}
                sx={{
                    width: rest.fullWidth ? '100%' : undefined,
                    ...(rest.justifyContent ? { justifyContent: rest.justifyContent } : {})
                }}
            >
                {toRender}
            </StyledBox>
        );
    }
};
